import { useEffect, useState, useTransition } from "react";
import Icon from "./icon";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSettingStore } from "@/store/setting-store";
import { useProfileStore } from "@/store/profile-store";
import { NAV_ITEMS as navItems } from "./nav-items";

function useTabNavigate() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [, startTransition] = useTransition();
  const [pendingHref, setPendingHref] = useState<string | null>(null);

  // Drop the optimistic target once the URL actually catches up to it.
  useEffect(() => {
    if (pendingHref && pathname === pendingHref) setPendingHref(null);
  }, [pathname, pendingHref]);

  const onTabClick = (e: React.MouseEvent, href: string, isActive: boolean) => {
    // Let the browser handle modified clicks (open in new tab, etc.).
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    if (isActive) return;
    setPendingHref(href); // urgent: highlight moves right away
    startTransition(() => navigate(href));
  };

  return { onTabClick, activeHref: pendingHref ?? pathname };
}

/**
 * How tall the bar is above the device's bottom safe area. Both styles land
 * within a couple of pixels of each other, so anything floating above the bar
 * can work off one number.
 */
export const BOTTOM_BAR_HEIGHT = 68;

/**
 * A CSS length that clears both the bar and the safe area under it. Use it as
 * the `bottom` of anything that must float just above the navigation — the var
 * is injected by the Eitaa WebApp SDK and falls back to 0 on the web.
 */
export const BOTTOM_BAR_CLEARANCE =
  `calc(${BOTTOM_BAR_HEIGHT}px + var(--tg-safe-area-inset-bottom, 0px))`;

export default function BottomBar() {
  const bottomBarStyle = useSettingStore((s) => s.bottomBarStyle);

  return bottomBarStyle === "floating" ? <FloatingBar /> : <ClassicBar />;
}


function ClassicBar() {
  const avatar = useProfileStore((s) => s.profile?.avatar);
  const { onTabClick, activeHref } = useTabNavigate();

  return (
    <nav className="fixed bottom-0 px-4 left-0 right-0 w-full max-w-xl z-50 mx-auto flex flex-col justify-center items-center bg-background border-t border-default-100">
      <div className="w-full flex justify-around items-center rounded-full">
        {navItems.map((item) => {
          const isActive = activeHref === item.href;
          const showAvatar = item.name === "profile" && Boolean(avatar);
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={(e) => onTabClick(e, item.href, isActive)}
              className="relative flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors py-3"
            >
              {showAvatar ? (
                <img
                  src={avatar!}
                  alt="profile"
                  width={24}
                  height={24}
                  loading="lazy"
                  decoding="async"
                  className={`size-6 rounded-full object-cover ring-2 ${
                    isActive ? "ring-primary" : "ring-transparent"
                  }`}
                />
              ) : (
                <Icon
                  name={isActive ? item.activeIcon : item.icon}
                  className={isActive ? "text-primary" : "text-default-400"}
                />
              )}
              <span
                className={`text-[10px] ${
                  isActive ? "text-default-900" : "text-default-400"
                }`}
              >
                {item.label}
              </span>
              {item.dot && (
                <span className="absolute right-[28%] top-2 size-2 rounded-full border-2 border-background bg-danger" />
              )}
            </Link>
          );
        })}
      </div>
      <div className="safe-area-inset-bottom w-full"></div>
    </nav>
  );
}

function FloatingBar() {
  const avatar = useProfileStore((s) => s.profile?.avatar);
  const { onTabClick, activeHref } = useTabNavigate();

  const count = navItems.length;
  const activeIndex = Math.max(
    0,
    navItems.findIndex((i) => i.href === activeHref),
  );

  const rtl = typeof document !== "undefined" && document.dir === "rtl";
  const offsetPercent = activeIndex * (rtl ? -100 : 100);

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-50 flex flex-col items-center justify-center px-4 py-2 pointer-events-none`}
    >
      <div className="pointer-events-auto relative flex w-full max-w-xs rounded-full p-1 bottom-bar-blur">
        {/* Sliding active highlight — transform only */}
        <span
          aria-hidden
          className="absolute top-1 bottom-1 start-1 rounded-full bg-primary/10 transition-transform duration-300 ease-out will-change-transform"
          style={{
            width: `calc((100% - 0.5rem) / ${count})`,
            transform: `translateX(${offsetPercent}%)`,
          }}
        />

        {navItems.map((item) => {
          const isActive = activeHref === item.href;
          const showAvatar = item.name === "profile" && Boolean(avatar);
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={(e) => onTabClick(e, item.href, isActive)}
              aria-label={item.label}
              aria-current={isActive ? "page" : undefined}
              className="relative z-10 flex flex-1 flex-col items-center justify-center gap-1 rounded-full py-1"
            >
              {showAvatar ? (
                <img
                  src={avatar!}
                  alt="profile"
                  width={20}
                  height={20}
                  loading="lazy"
                  decoding="async"
                  className={`size-5 rounded-full object-cover`}
                />
              ) : (
                <Icon
                size={20}
                  name={isActive ? item.activeIcon : item.icon}
                  className={`transition-colors duration-200 ${
                    isActive ? "text-primary" : "text-default-800"
                  }`}
                />
              )}

              <span
                className={`text-[10px] font-semibold leading-none transition-colors duration-200 ${
                  isActive ? "text-primary" : "text-default-700"
                }`}
              >
                {item.label}
              </span>

              {item.dot && (
                <span className="absolute right-3 top-1.5 size-2 rounded-full border-2 border-white bg-danger" />
              )}
            </Link>
          );
        })}
      </div>
      <div className="safe-area-inset-bottom w-full"></div>
    </nav>
  );
}
